import { HStack, PageContainer, PageHeader, Text, VStack } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { useCrtVersionSelection } from '@/hooks/crt/useCrtVersionSelection'
import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import { parseDateOnly } from '@/utils/DateOnlyHelpers'
import SearchField from '@/components/DataTable/FilterControls/Filters/SearchField'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CrtVersionsTable } from './-components/CrtVersionsTable'

export const Route = createFileRoute('/_auth/_modules/crt/kpi-reports/versions/')({
  component: RouteComponent,
})

/**
 * Manages the versions of the period picked in the header: rename, change status, retry a failed compute,
 * delete, and load one into the P&L Summary. It replaces the P&L footer's former "Load version" dialog and is
 * reached from the header's version selector.
 *
 * The period is the global selection, not a URL param, so changing it in the header refreshes this list the
 * same way it refreshes the P&L.
 */
function RouteComponent() {
  const { t } = useTranslation(['crt', 'common'])
  const { formatMonthYear } = useLocalizedDate()
  const { selection } = useCrtVersionSelection()
  const [search, setSearch] = useState('')

  return (
    <PageContainer>
      <VStack gap="lg" align="stretch">
        <VStack gap="xs">
          <PageHeader>{t('route.crt.versions', { ns: 'common' })}</PageHeader>
          <Text tone="muted">
            {t('crtVersions.page.description', {
              period: formatMonthYear(parseDateOnly(selection.crtPeriod)),
            })}
          </Text>
        </VStack>

        {/* The search and the table share a card, like the app's other list pages. */}
        <Card>
          <CardContent className="pt-6">
            <VStack gap="md" align="stretch">
              {/* The data tables' search field, right-aligned as theirs is. It filters as you type, so its
                  button has nothing more to do. */}
              <HStack className="justify-end">
                <SearchField
                  className="w-72"
                  placeholder={t('comboboxSearch.searchPlaceholder', { ns: 'common' })}
                  value={search}
                  setValue={setSearch}
                  onSearch={() => {}}
                />
              </HStack>
              <div className="bg-background-level-1 rounded-md border">
                <CrtVersionsTable crtPeriod={selection.crtPeriod} search={search} />
              </div>
            </VStack>
          </CardContent>
        </Card>
      </VStack>
    </PageContainer>
  )
}
